/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  MenuItem,
  Button,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import Drawer from "@mui/material/Drawer";
import style from "../../../styles/common/formModal.module.css";
import EditDialog from "../../../Components/Common/EditDialog";
import FileField from "../../../Components/Masters/FileField";
import formatTitleWithOptional from "../../Scope1/OptionalFeild";
import { useUser } from "../../../context/AuthContext";
import Multiline from "../../../Components/Masters/Multiline";
import { get } from "../../../utils/api";

const parseMonthYear = (value) => {
  const monthNameToNumber = {
    jan: 1,
    january: 1,
    feb: 2,
    february: 2,
    mar: 3,
    march: 3,
    apr: 4,
    april: 4,
    may: 5,
    jun: 6,
    june: 6,
    jul: 7,
    july: 7,
    aug: 8,
    august: 8,
    sep: 9,
    sept: 9,
    september: 9,
    oct: 10,
    october: 10,
    nov: 11,
    november: 11,
    dec: 12,
    december: 12,
  };

  if (value && typeof value === "object") {
    // Already normalized object: { month, year }
    if (value.month && value.year) {
      return {
        month: Number(value.month),
        year: String(value.year),
      };
    }

    // Date-like object (Date instance or serializable date)
    const dateValue = new Date(value);
    if (!Number.isNaN(dateValue.getTime())) {
      return {
        month: dateValue.getMonth() + 1,
        year: String(dateValue.getFullYear()),
      };
    }

    return {
      month: value.month || "",
      year: value.year || "",
    };
  }

  if (typeof value === "string") {
    const normalized = value.trim();

    // YYYY-MM
    const yearMonthMatch = normalized.match(/^(\d{4})-(\d{1,2})$/);
    if (yearMonthMatch) {
      return { year: yearMonthMatch[1], month: Number(yearMonthMatch[2]) };
    }

    // MonthName YYYY (e.g., "Mar 2026")
    const monthNameYearMatch = normalized.match(/^([A-Za-z]+)\s+(\d{4})$/);
    if (monthNameYearMatch) {
      const monthNumber =
        monthNameToNumber[monthNameYearMatch[1].toLowerCase()] || "";
      if (monthNumber) {
        return { year: monthNameYearMatch[2], month: monthNumber };
      }
    }

    // Try generic Date parsing (handles ISO/date strings)
    const parsedDate = new Date(normalized);
    if (!Number.isNaN(parsedDate.getTime())) {
      return {
        month: parsedDate.getMonth() + 1,
        year: String(parsedDate.getFullYear()),
      };
    }
  }

  return { month: "", year: "" };
};

const monthList = [
  { id: 1, month: "Jan" },
  { id: 2, month: "Feb" },
  { id: 3, month: "Mar" },
  { id: 4, month: "Apr" },
  { id: 5, month: "May" },
  { id: 6, month: "Jun" },
  { id: 7, month: "Jul" },
  { id: 8, month: "Aug" },
  { id: 9, month: "Sep" },
  { id: 10, month: "Oct" },
  { id: 11, month: "Nov" },
  { id: 12, month: "Dec" },
];

const currentDate = new Date();
const currentMonth = currentDate.getMonth() + 1;
const currentYear = currentDate.getFullYear();

const isFutureMonthYear = (month, year) => {
  const parsedMonth = Number(month);
  const parsedYear = Number(year);

  if (!parsedMonth || !parsedYear) return false;
  if (parsedYear > currentYear) return true;
  return parsedYear === currentYear && parsedMonth > currentMonth;
};

const Form = ({ isOpen, onClose, onSubmit, header, initialData, isEditing }) => {
  const { user } = useUser();

  const pm =
    initialData?.productMaster && typeof initialData.productMaster === "object"
      ? initialData.productMaster
      : null;

  const weightFromMaster =
    pm?.averageWeightPerUnit ?? pm?.weight ?? initialData?.averageWeightPerUnit;

  const initialFormData = {
    productMaster:
      typeof initialData?.productMaster === "object"
        ? initialData?.productMaster?._id || initialData?.productMaster?.id || ""
        : initialData?.productMaster || "",
    productId: String(pm?.productId ?? initialData?.productId ?? "").trim(),
    productName: String(pm?.productName ?? initialData?.productName ?? "").trim(),
    eolMonth: parseMonthYear(initialData?.eolMonth),
    endOfLifeTreatmentMethod: String(
      pm?.disposalMethod ?? initialData?.endOfLifeTreatmentMethod ?? ""
    ).trim(),
    averageWeightPerUnit:
      weightFromMaster !== undefined &&
      weightFromMaster !== null &&
      weightFromMaster !== ""
        ? String(weightFromMaster)
        : "",
    numberOfUnitsSold: initialData?.numberOfUnitsSold ?? "",
    soldUnit: String(pm?.soldUnit ?? initialData?.soldUnit ?? "").trim(),
    remark: initialData?.remark || "",
    editRemarkReason: initialData?.editRemarkReason || "",
    documents: initialData?.documents || [],
  };

  const [editDialog, setEditDialog] = useState(false);
  const [formData, setFormData] = useState(initialFormData);
  const [formDataSnapshot, setFormDataSnapshot] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({});
  const [productMasterOptions, setProductMasterOptions] = useState([]);
  const commonFieldSx = {
    "& .MuiFormHelperText-root": {
      marginLeft: 0,
      marginRight: 0,
      lineHeight: 1.2,
      minHeight: "1.2em",
    },
    "& .MuiSelect-select": {
      fontStyle: "normal",
    },
  };
  const getAllowedYearsForMonth = (monthValue) => {
    const selectedMonth = Number(monthValue || 0);
    const maxAllowedYear =
      selectedMonth > currentMonth ? currentYear - 1 : currentYear;
    const startYear = maxAllowedYear - 5;
    return Array.from({ length: 6 }, (_, i) => startYear + i);
  };

  useEffect(() => {
    setFormData(initialFormData);
    setFormDataSnapshot(initialFormData);
    setUploadedFiles(initialData?.documents || []);
  }, [isOpen, initialData]);

  useEffect(() => {
    if (!isOpen) return;

    const fetchProductNames = async () => {
      try {
        const res = await get("/master/product/productsList");
        const products = Array.isArray(res?.data?.data) ? res.data.data : [];
        const options = products
          .filter(
            (item) =>
              typeof item?.productId === "string" &&
              item.productId.trim() !== "" &&
              typeof item?.productName === "string" &&
              item.productName.trim() !== ""
          )
          .map((item) => ({
            _id: item?._id ?? item?.id ?? "",
            productId: item.productId.trim(),
            productName: item.productName.trim(),
            averageWeightPerUnit: item?.averageWeightPerUnit ?? item?.weight ?? "",
            disposalMethod: item?.disposalMethod ?? "",
            soldUnit: item?.soldUnit ?? "",
          }));
        setProductMasterOptions(options);
      } catch (error) {
        console.error("Error fetching product names:", error);
        setProductMasterOptions([]);
      }
    };

    fetchProductNames();
  }, [isOpen]);

  useEffect(() => {
    if (!formData?.productId && formData?.productName && productMasterOptions.length > 0) {
      const matchedProduct = productMasterOptions.find(
        (item) => item.productName === formData.productName
      );
      if (matchedProduct?.productId) {
        setFormData((prev) => ({
          ...prev,
          productId: matchedProduct.productId,
          productMaster:
            matchedProduct._id != null && matchedProduct._id !== ""
              ? String(matchedProduct._id)
              : prev.productMaster,
          soldUnit:
            matchedProduct?.soldUnit !== ""
              ? String(matchedProduct?.soldUnit)
              : "",
        }));
      }
    }
  }, [formData?.productId, formData?.productName, productMasterOptions]);

  const fieldConfig = [
    {
      label: "Product Id",
      name: "productId",
      type: "select",
      required: true,
      placeholder: "Select product id",
    },
    {
      label: "Product Name",
      name: "productName",
      type: "text",
      required: true,
      placeholder: "Product name",
      disabled: true,
    },
    {
      label: "Production Month",
      name: "eolMonth",
      type: "date",
      required: true,
    },
    {
      label: "End-of-Life Treatment Method",
      name: "endOfLifeTreatmentMethod",
      type: "text",
      required: true,
      placeholder: "Enter EOL treatment method",
      disabled: true,
    },
    {
      label: "Average Weight per Unit (MT)",
      name: "averageWeightPerUnit",
      type: "number",
      required: false,
      placeholder: "Enter average weight per unit",
      disabled: true,
    },
    {
      label: "Number of Units Sold",
      name: "numberOfUnitsSold",
      type: "numberRowWithSoldUnit",
      required: true,
      placeholder: "Enter number of units sold",
      soldUnitTitle: "Sold Unit",
    },
    {
      label: "Remark (optional)",
      name: "remark",
      type: "text",
      required: false,
      placeholder: "Enter remark",
    },
    {
      name: "editRemarkReason",
      type: "multlyline",
      placeholder: "Additional edit remarks",
      require: true,
    },
    {
      name: "document",
      type: "docs",
      placeholder: "Upload Documents",
    },
  ];

  const handleExitAgree = () => {
    setEditDialog(false);
    setErrors({});
    setFormData(initialFormData);
    setFormDataSnapshot(initialFormData);
    setUploadedFiles([]);
    onClose();
  };

  const isFormChanged = () =>
    JSON.stringify(formData) !== JSON.stringify(formDataSnapshot);

  const handleClose = () => {
    if (!isFormChanged()) {
      handleExitAgree();
    } else {
      setEditDialog(true);
    }
  };

  const validateFormData = () => {
    const newErrors = {};

    fieldConfig.forEach((field) => {
      if (field?.name === "editRemarkReason") {
        return;
      }

      const value = formData[field.name];
      if (field.type === "date") {
        const month = formData?.[field.name]?.month;
        const year = formData?.[field.name]?.year;
        if (!month || !year) {
          newErrors[field.name] = {
            month: !month ? "Month is required." : "",
            year: !year ? "Year is required." : "",
          };
        }
        return;
      }

      if (field.type === "numberRowWithSoldUnit") {
        const value = formData.numberOfUnitsSold;
        if (field.required) {
          if (value === "" || value === null || value === undefined) {
            newErrors.numberOfUnitsSold = `${field.label} is required.`;
            return;
          }
        }
        if (value !== "" && value != null && isNaN(Number(value))) {
          newErrors.numberOfUnitsSold = `${field.label} must be a number.`;
        }
        return;
      }

      if (field.required) {
        if (value === "" || value === null || value === undefined) {
          newErrors[field.name] = `${field.label} is required.`;
          return;
        }
      }

      if (field.type === "number" && value !== "" && isNaN(Number(value))) {
        newErrors[field.name] = `${field.label} must be a number.`;
      }
    });

    if (!String(formData?.productMaster || "").trim()) {
      newErrors.productId =
        "Product master is required. Select a product from the list.";
    }

    if (user?.endUser && isEditing) {
      const remark = String(formData?.editRemarkReason ?? "").trim();
      if (!remark) {
        newErrors.editRemarkReason = "Edit remark is required";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (name, type) => (event) => {
    const { value } = event.target;

    if (type === "number" && value !== "" && !/^\d*\.?\d*$/.test(value)) return;

    if (name === "productId") {
      const selectedProduct = productMasterOptions.find(
        (item) => item.productId === value
      );

      setFormData((prev) => ({
        ...prev,
        productId: value,
        productMaster:
          selectedProduct?._id != null && selectedProduct._id !== ""
            ? String(selectedProduct._id)
            : "",
        productName: selectedProduct?.productName || "",
        endOfLifeTreatmentMethod:
          selectedProduct?.disposalMethod !== ""
            ? String(selectedProduct?.disposalMethod)
            : "",
        averageWeightPerUnit:
          selectedProduct?.averageWeightPerUnit !== ""
            ? String(selectedProduct?.averageWeightPerUnit)
            : "",
        soldUnit:
          selectedProduct?.soldUnit !== ""
            ? String(selectedProduct?.soldUnit)
            : "",
      }));

      setErrors((prev) => ({
        ...prev,
        productId: "",
        productName: "",
        endOfLifeTreatmentMethod: "",
        averageWeightPerUnit: "",
        numberOfUnitsSold: "",
      }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  const handleMonthYearChange = (fieldName, key) => (event) => {
    const rawValue = event.target.value;
    const value = key === "month" ? Number(rawValue) : rawValue;

    setFormData((prev) => {
      const prevMonth = prev?.[fieldName]?.month || "";
      const prevYear = prev?.[fieldName]?.year || "";
      const nextMonth = key === "month" ? value : prevMonth;
      const nextYear = key === "year" ? value : prevYear;

      if (isFutureMonthYear(nextMonth, nextYear)) {
        return prev;
      }

      const next = {
        ...prev,
        [fieldName]: {
          ...(prev?.[fieldName] || {}),
          [key]: value,
        },
      };

      return next;
    });

    setErrors((prev) => ({
      ...prev,
      [fieldName]: {
        ...(prev?.[fieldName] || {}),
        [key]: "",
      },
    }));
  };

  const handleSubmit = () => {
    if (!validateFormData()) return;
    if (isFutureMonthYear(formData?.eolMonth?.month, formData?.eolMonth?.year)) {
      setErrors((prev) => ({
        ...prev,
        eolMonth: {
          ...(prev?.eolMonth || {}),
          month: "Future month is not allowed.",
        },
      }));
      return;
    }

    let formDataToSubmit = {
      ...formData,
      eolMonth:
        formData?.eolMonth?.month && formData?.eolMonth?.year
          ? `${formData.eolMonth.year}-${String(
              formData.eolMonth.month
            ).padStart(2, "0")}`
          : "",
      documents: uploadedFiles,
    };

    if (user?.endUser && isEditing) {
      formDataToSubmit.requestStatus = "edit";
      formDataToSubmit.requestMode = true;
      formDataToSubmit.approvalStatus = "Pending";
    }

    onSubmit(formDataToSubmit, isEditing);
    setUploadedFiles([]);
  };

  return (
    <Drawer
      sx={{
        backgroundColor: "transparent",
        "& .MuiDrawer-paper": {
          backgroundColor: "transparent",
          boxShadow: "none",
          height: "100vh",
          minHeight: "100vh",
          overflowY: "auto",
          overflowX: "hidden",
          display: "flex",
          flexDirection: "column",
        },
      }}
      anchor="right"
      open={isOpen}
      onClose={handleClose}
    >
      <Box
        sx={{
          width: "26rem",
          display: "flex",
          justifyContent: "space-between",
          gap: "1rem",
        }}
      >
        {editDialog && (
          <EditDialog {...{ editDialog, setEditDialog, handleExitAgree }} />
        )}
        <Box
          sx={{
            backgroundColor: "#fff",
            height: "20px",
            padding: "13px",
            margin: "30px 0 0 0",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            borderRadius: "80%",
            width: "5%",
            zIndex: "1",
          }}
        >
          <IconButton onClick={handleClose} sx={{ color: "black" }}>
            <CloseIcon sx={{ fontSize: "1.2rem" }} />
          </IconButton>
        </Box>
        <Box className={style.main_div}>
          <Typography
            id="modal-title"
            variant="h6"
            align="left"
            sx={{ textDecoration: "underline", fontWeight: "bold", mb: 2 }}
          >
            {header}
          </Typography>

          <form>
            {fieldConfig.map((field) => {
              if (field?.name === "editRemarkReason" && !(user?.endUser && isEditing)) {
                return null;
              }

              return (
              <div key={field.name} style={{ margin: "1rem 0" }}>
                {user?.endUser && isEditing && field?.type === "multlyline" ? (
                  <Multiline
                    title={formatTitleWithOptional("Edit Remark Reason")}
                    placeholder="Additional edit remarks"
                    fieldName="editRemarkReason"
                    value={formData.editRemarkReason}
                    handleChange={handleChange("editRemarkReason")}
                    errors={errors.editRemarkReason}
                  />
                ) : field.type === "docs" ? (
                  <FileField
                    title={formatTitleWithOptional("Upload Documents (optional)")}
                    name={field.name}
                    errors={errors[field.name]}
                    fileUrls={uploadedFiles}
                    progress={uploadProgress[field.name] || []}
                    {...{
                      setUploadedFiles,
                      setUploadProgress,
                      setErrors,
                    }}
                  />
                ) : (
                  <>
                    {field.type === "date" ? (
                      <>
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: "bold", mt: 1 }}
                        >
                          {field.label}
                        </Typography>
                        <Box sx={{ display: "flex", gap: 1 }}>
                          <TextField
                            select
                            size="small"
                            sx={{ minWidth: "48%", ...commonFieldSx }}
                            value={formData?.[field.name]?.month || ""}
                            onChange={handleMonthYearChange(field.name, "month")}
                            error={Boolean(errors?.[field.name]?.month)}
                            helperText={errors?.[field.name]?.month || ""}
                            SelectProps={{
                              displayEmpty: true,
                              MenuProps: {
                                PaperProps: {
                                  style: {
                                    maxHeight: "55%",
                                    overflowY: "auto",
                                  },
                                },
                              },
                            }}
                          >
                            <MenuItem value="" sx={{ fontStyle: "normal" }}>
                              Month
                            </MenuItem>
                            {monthList
                              .filter((item) => {
                                const selectedYear = Number(
                                  formData?.[field.name]?.year || 0
                                );
                                if (!selectedYear || selectedYear < currentYear) {
                                  return true;
                                }
                                return item.id <= currentMonth;
                              })
                              .map((item) => {
                              return (
                                <MenuItem key={item.id} value={item.id}>
                                {item.month}
                                </MenuItem>
                              );
                            })}
                          </TextField>
                          <TextField
                            select
                            size="small"
                            sx={{ minWidth: "48%", ...commonFieldSx }}
                            value={formData?.[field.name]?.year || ""}
                            onChange={handleMonthYearChange(field.name, "year")}
                            error={Boolean(errors?.[field.name]?.year)}
                            helperText={errors?.[field.name]?.year || ""}
                            SelectProps={{
                              displayEmpty: true,
                              MenuProps: {
                                PaperProps: {
                                  style: {
                                    maxHeight: "55%",
                                    overflowY: "auto",
                                  },
                                },
                              },
                            }}
                          >
                            <MenuItem value="" sx={{ fontStyle: "normal" }}>
                              Year
                            </MenuItem>
                            {getAllowedYearsForMonth(
                              formData?.[field.name]?.month
                            ).map((year) => (
                              <MenuItem key={year} value={String(year)}>
                                {year}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Box>
                      </>
                    ) : field.type === "numberRowWithSoldUnit" ? null : (
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: "bold", mt: 1 }}
                      >
                        {formatTitleWithOptional(field.label)}
                      </Typography>
                    )}

                    {field.type === "select" ? (
                      <TextField
                        select
                        size="small"
                        fullWidth
                        sx={commonFieldSx}
                        required={field.required}
                        value={formData[field.name] || ""}
                        onChange={handleChange(field.name, field.type)}
                        error={Boolean(errors[field.name])}
                        helperText={errors[field.name] || ""}
                        SelectProps={{ displayEmpty: true }}
                      >
                        <MenuItem value="" sx={{ fontStyle: "normal" }}>
                          {field.placeholder || "Select"}
                        </MenuItem>
                        {productMasterOptions.map((item) => (
                          <MenuItem key={item.productId} value={item.productId}>
                            {item.productId}
                          </MenuItem>
                        ))}
                      </TextField>
                    ) : field.type === "date" ? null : field.type === "numberRowWithSoldUnit" ? (
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "1fr minmax(5.5rem, 26%)",
                          gap: 1,
                          alignItems: "flex-start",
                        }}
                      >
                        <Box sx={{ minWidth: 0 }}>
                          <Typography
                            variant="subtitle1"
                            sx={{ fontWeight: "bold", mt: 1 }}
                          >
                            {formatTitleWithOptional(field.label)}
                          </Typography>
                          <TextField
                            type="text"
                            inputMode="decimal"
                            size="small"
                            fullWidth
                            required={field.required}
                            placeholder={field.placeholder || ""}
                            value={formData.numberOfUnitsSold ?? ""}
                            onChange={handleChange("numberOfUnitsSold", "number")}
                            error={Boolean(errors.numberOfUnitsSold)}
                            helperText={errors.numberOfUnitsSold || ""}
                            sx={commonFieldSx}
                          />
                        </Box>
                        <Box sx={{ minWidth: 0, maxWidth: "100%" }}>
                          <Typography
                            variant="subtitle1"
                            sx={{ fontWeight: "bold", mt: 1 }}
                          >
                            {field.soldUnitTitle || "Sold Unit"}
                          </Typography>
                          <TextField
                            size="small"
                            fullWidth
                            disabled
                            value={formData.soldUnit || ""}
                            placeholder="Unit"
                            sx={{
                              ...commonFieldSx,
                              "& .MuiInputBase-input.Mui-disabled": {
                                WebkitTextFillColor: "#6b7280",
                                color: "#6b7280",
                              },
                              "& .MuiInputBase-root.Mui-disabled": {
                                backgroundColor: "#f3f4f6",
                              },
                            }}
                          />
                        </Box>
                      </Box>
                    ) : (
                      <TextField
                        type={field.type === "number" ? "text" : "text"}
                        size="small"
                        fullWidth
                        required={field.required}
                        placeholder={field.placeholder || ""}
                        multiline={["remark"].includes(field.name)}
                        minRows={["remark"].includes(field.name) ? 2 : 1}
                        value={formData[field.name] || ""}
                        onChange={handleChange(field.name, field.type)}
                        disabled={Boolean(field.disabled)}
                        error={Boolean(errors[field.name])}
                        helperText={errors[field.name] || ""}
                        sx={{
                          ...commonFieldSx,
                          ...(Boolean(field.disabled) && {
                            "& .MuiInputBase-input.Mui-disabled": {
                              WebkitTextFillColor: "#6b7280",
                              color: "#6b7280",
                              cursor: "not-allowed",
                            },
                            "& .MuiInputBase-root.Mui-disabled": {
                              backgroundColor: "#f3f4f6",
                              cursor: "not-allowed",
                            },
                          }),
                        }}
                      />
                    )}
                  </>
                )}
              </div>
              );
            })}
          </form>

          <Button
            onClick={handleSubmit}
            variant="contained"
            sx={{ backgroundColor: "#1C486B", textTransform: "none" }}
          >
            Submit
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};

export default Form;

